import { useEffect, useState } from 'react'
import { ROLE_LABELS, SecurityRole, UserStatus } from '../constants'
import { TeamNoStoreSetting } from '../components/NoStoreSettings'
import { DualControlFallbackModal, TeamPromotionApprovalSetting } from '../components/templates/PromotionApproval'
import { useAuth } from '../contexts/AuthContext'
import { settingsApiService, type DeactivateImpact, type DualControlFallback } from '../services/settingsApiService'
import { ApiError } from '../services/BaseApiService'
import type { TeamMember } from '../types'
import { colors, fontSize, radius, shadow, spacing, tracking } from '../styles/theme'
import { Select, UserStatusChip, useDialogs } from '../components/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShield } from '@fortawesome/free-solid-svg-icons'
import { teamApiService } from '../services/teamApiService'
import { CloseTeamModal } from '../components/team/CloseTeamModal'

const ROLE_OPTIONS = [
  { value: SecurityRole.VIEWER, label: 'Viewer' },
  { value: SecurityRole.EDITOR, label: 'Editor' },
  { value: SecurityRole.TEAM_ADMIN, label: 'Team Admin' },
]
const INVITE_ROLE_OPTIONS = [
  { value: SecurityRole.VIEWER, label: 'Viewer — can view templates and render jobs' },
  { value: SecurityRole.EDITOR, label: 'Editor — can create and edit templates' },
  { value: SecurityRole.TEAM_ADMIN, label: 'Team Admin — full team management access' },
]

/**
 * Leave the team (every member). The only Team Admin is refused; a leave that would make dual control unsatisfiable
 * asks which approval level replaces it. Afterwards the account is on no team and lands on the create-or-join page.
 */
function LeaveTeamCard() {
  const { refresh } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [needsFallback, setNeedsFallback] = useState(false)
  const dialogs = useDialogs()

  async function leave(fallback?: DualControlFallback) {
    if (!fallback && !(await dialogs.confirm({
      title: 'Leave this team?',
      message: 'You keep your login, but lose access to its templates and documents. What you authored stays with the team.',
      confirmLabel: 'Leave team', danger: true,
    }))) return
    setBusy(true); setError('')
    try {
      await teamApiService.leave(fallback)
      await refresh()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DUAL_CONTROL_FALLBACK_REQUIRED') setNeedsFallback(true)
      else setError(err instanceof ApiError ? err.message : 'Leaving failed.')
    } finally {
      setBusy(false)
    }
  }

  const button: React.CSSProperties = {
    padding: '6px 14px', background: 'none', border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
    fontSize: fontSize.sm, cursor: 'pointer', color: colors.error, opacity: busy ? 0.6 : 1,
  }
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.lg, marginBottom: spacing.xl }}>
      <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>Leave team</div>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
        You keep your login and can create or join another team. Templates and versions you authored stay with this team.
      </div>
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm, marginBottom: spacing.sm }}>{error}</div>}
      {needsFallback ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          <div style={{ fontSize: fontSize.sm, color: colors.text }}>
            Leaving leaves one editor or admin who could approve promotions, so dual control can no longer be satisfied. Replace it with:
          </div>
          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            <button style={button} disabled={busy} onClick={() => leave('auto')}>Automatic approval, then leave</button>
            <button style={button} disabled={busy} onClick={() => leave('self')}>Self-approval, then leave</button>
          </div>
        </div>
      ) : (
        <button style={button} disabled={busy} onClick={() => leave()}>{busy ? 'Leaving…' : 'Leave team'}</button>
      )}
      {dialogs.element}
    </div>
  )
}

/** Team Admin: close the team (see CloseTeamModal). */
function DangerZone() {
  const { refresh } = useAuth()
  const [open, setOpen] = useState(false)
  return (
    <div style={{ border: `1px solid ${colors.errorBorder}`, borderRadius: radius.md, padding: spacing.lg, marginBottom: spacing.xl }}>
      <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.error, marginBottom: spacing.xs }}>Danger zone</div>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
        Closing the team stops all work, charges the final bill for this month and erases the team's data after 30 days.
        A Team Admin can reopen it until then.
      </div>
      <button onClick={() => setOpen(true)} style={{ padding: '6px 14px', background: colors.error, color: '#fff', border: 'none', borderRadius: radius.sm, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
        Close team
      </button>
      <CloseTeamModal open={open} onClose={() => setOpen(false)} onClosed={async () => { setOpen(false); await refresh() }} />
    </div>
  )
}

export default function TeamPage() {
  const { user: me } = useAuth()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(20)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState('')
  const [error, setError] = useState('')
  const [members, setMembers] = useState<TeamMember[]>([])
  const [transferring, setTransferring] = useState<string | null>(null)
  const [changingRole, setChangingRole] = useState<string | null>(null)
  const dialogs = useDialogs()

  async function loadMembers() {
    try {
      const list = await settingsApiService.listTeamUsers()
      setMembers(list)
    } catch { /* non-fatal */ }
  }

  const isAdminUser = (me?.securityRole ?? 0) >= SecurityRole.TEAM_ADMIN
  const teamClosed = !!me?.teamClosedAt
  useEffect(() => { if (isAdminUser && !teamClosed) loadMembers() }, [isAdminUser, teamClosed])

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setSending(true); setError(''); setSent('')
    try {
      const res = await settingsApiService.invite(email.trim(), role)
      setSent(`Invite sent to ${res.email}`)
      setEmail('')
      loadMembers()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send invite')
    } finally {
      setSending(false)
    }
  }

  async function handleChangeRole(targetId: string, email: string, currentRole: number, newRole: number) {
    if (newRole === currentRole) return
    setChangingRole(targetId)
    const lowering = newRole < SecurityRole.EDITOR && currentRole >= SecurityRole.EDITOR
    try {
      if (lowering) {
        const impact = await settingsApiService.updateRoleImpact(targetId, newRole)
        if (impact.fallbackRequired) { openRoleFallback(targetId, email, newRole, impact); return }
      }
      await settingsApiService.updateRole(targetId, newRole)
      await loadMembers()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'DUAL_CONTROL_FALLBACK_REQUIRED') {
        try { openRoleFallback(targetId, email, newRole, await settingsApiService.updateRoleImpact(targetId, newRole)) } catch { void dialogs.alert(err.message) }
        return
      }
      void dialogs.alert(err instanceof ApiError ? err.message : 'Role change failed')
    } finally {
      setChangingRole(null)
    }
  }

  async function handleTransfer(targetId: string, targetEmail: string) {
    if (!(await dialogs.confirm({ title: `Transfer Admin role to ${targetEmail}?`, message: 'You will become an Editor.', confirmLabel: 'Transfer Admin' }))) return
    setTransferring(targetId)
    try {
      await settingsApiService.transferAdmin(targetId)
      await loadMembers()
    } catch (err) {
      void dialogs.alert(err instanceof ApiError ? err.message : 'Transfer failed')
    } finally {
      setTransferring(null)
    }
  }

  // newRole set: a role change; otherwise a deactivate.
  const [fallbackFor, setFallbackFor] = useState<{ userId: string; email: string; impact: DeactivateImpact; newRole?: number } | null>(null)
  const [fallbackSaving, setFallbackSaving] = useState(false)
  const [fallbackError, setFallbackError] = useState('')
  // Remounts the team approval setting, which the fallback may have changed.
  const [approvalKey, setApprovalKey] = useState(0)

  async function openFallback(userId: string, email: string, impact?: DeactivateImpact) {
    setFallbackError('')
    setFallbackFor({ userId, email, impact: impact ?? await settingsApiService.deactivateMemberImpact(userId) })
  }

  function openRoleFallback(userId: string, email: string, newRole: number, impact: DeactivateImpact) {
    setFallbackError('')
    setFallbackFor({ userId, email, impact, newRole })
  }

  async function handleDeactivate(targetId: string, email: string) {
    try {
      const impact = await settingsApiService.deactivateMemberImpact(targetId)
      if (impact.fallbackRequired) { await openFallback(targetId, email, impact); return }
      if (!(await dialogs.confirm({ title: `Deactivate ${email}?`, message: 'They lose access until reactivated.', confirmLabel: 'Deactivate', danger: true }))) return
      await settingsApiService.deactivateMember(targetId)
      await loadMembers()
    } catch (err) {
      // Dual control came into use between the impact check and the deactivate.
      if (err instanceof ApiError && err.status === 409 && err.code === 'DUAL_CONTROL_FALLBACK_REQUIRED') {
        try { await openFallback(targetId, email) } catch { void dialogs.alert(err.message) }
        return
      }
      void dialogs.alert(err instanceof ApiError ? err.message : 'Deactivate failed')
    }
  }

  async function confirmFallback(fallback: DualControlFallback) {
    if (!fallbackFor) return
    const roleChange = fallbackFor.newRole !== undefined
    setFallbackSaving(true); setFallbackError('')
    try {
      if (roleChange) await settingsApiService.updateRole(fallbackFor.userId, fallbackFor.newRole!, fallback)
      else await settingsApiService.deactivateMember(fallbackFor.userId, fallback)
      setFallbackFor(null)
      setApprovalKey(k => k + 1)
      await loadMembers()
    } catch (err) {
      setFallbackError(err instanceof ApiError ? err.message : roleChange ? 'Role change failed' : 'Deactivate failed')
    } finally {
      setFallbackSaving(false)
    }
  }

  async function handleReactivate(targetId: string) {
    try { await settingsApiService.reactivateMember(targetId); await loadMembers() }
    catch (err) { void dialogs.alert(err instanceof ApiError ? err.message : 'Reactivate failed') }
  }

  const [teamName, setTeamName] = useState('')
  const [renaming, setRenaming] = useState(false)
  async function handleRename(e: React.FormEvent) {
    e.preventDefault()
    if (!teamName.trim()) return
    setRenaming(true)
    try { await settingsApiService.updateTeam(teamName.trim()); setTeamName(''); void dialogs.alert({ title: 'Team renamed', message: `The team is now called ${teamName.trim()}.` }) }
    catch (err) { void dialogs.alert(err instanceof ApiError ? err.message : 'Rename failed') }
    finally { setRenaming(false) }
  }

  const isAdmin = (me?.securityRole ?? 0) >= SecurityRole.TEAM_ADMIN

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontSize: fontSize.sm,
    fontWeight: 600,
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.sm,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  }

  const heading = (
    <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight, marginBottom: spacing.xl }}>
      Team
    </h1>
  )

  // Members who are not admins, and everyone on a closed team, can only leave here.
  if (!isAdmin || teamClosed) {
    return (
      <div>
        {heading}
        <div style={{ maxWidth: 620 }}>
          <LeaveTeamCard />
        </div>
      </div>
    )
  }

  return (
    <div>
      {heading}

      <div style={{ maxWidth: 620 }}>
        {isAdmin && <TeamNoStoreSetting />}
        {isAdmin && <TeamPromotionApprovalSetting key={approvalKey} />}
        {isAdmin && (
          <form onSubmit={handleRename} style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginBottom: spacing.xl }}>
            <input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Rename team…"
              style={{ flex: 1, padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.base, color: colors.text, boxSizing: 'border-box', outline: 'none' }} />
            <button type="submit" disabled={renaming || !teamName.trim()}
              style={{ padding: '8px 16px', background: colors.primary, color: colors.onPrimary, border: 'none', borderRadius: radius.sm, fontSize: fontSize.base, fontWeight: 600, cursor: 'pointer', opacity: renaming || !teamName.trim() ? 0.6 : 1 }}>
              {renaming ? 'Saving…' : 'Rename'}
            </button>
          </form>
        )}
        {members.length > 0 && (
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            overflow: 'hidden',
            marginBottom: spacing.xl,
          }}>
            <div style={{ padding: `${spacing.md} ${spacing.lg}`, borderBottom: `1px solid ${colors.border}` }}>
              <span style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text }}>Team Members</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name / Email</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.emailAddress
                  const isSelf = m.userId === me?.userId
                  const canTransfer = isAdmin && !isSelf && m.role < SecurityRole.TEAM_ADMIN && m.status === UserStatus.ACTIVE
                  const canChangeRole = isAdmin && !isSelf && m.status === UserStatus.ACTIVE
                  return (
                    <tr key={m.userId}>
                      <td style={tdStyle}>
                        <div>{name}</div>
                        {name !== m.emailAddress && (
                          <div style={{ fontSize: '11px', color: colors.textSecondary }}>{m.emailAddress}</div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {canChangeRole ? (
                          <div style={{ width: 160, opacity: changingRole === m.userId ? 0.5 : 1 }}>
                            <Select
                              inputSize="sm"
                              value={m.role}
                              isDisabled={changingRole === m.userId}
                              onChange={v => handleChangeRole(m.userId, m.emailAddress, m.role, Number(v))}
                              options={ROLE_OPTIONS}
                            />
                          </div>
                        ) : (
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: m.role >= SecurityRole.TEAM_ADMIN ? colors.primary : colors.textSecondary,
                          }}>
                            {ROLE_LABELS[m.role] ?? `Role ${m.role}`}
                            {isSelf && <span style={{ color: colors.textSecondary, fontWeight: 400 }}> (you)</span>}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, display: 'flex', gap: 8 }}>
                        {canTransfer && (
                          <button
                            onClick={() => handleTransfer(m.userId, m.emailAddress)}
                            disabled={transferring === m.userId}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px',
                              background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                              fontSize: fontSize.sm, cursor: 'pointer', color: colors.text,
                              opacity: transferring === m.userId ? 0.5 : 1,
                            }}
                          >
                            <FontAwesomeIcon icon={faShield} style={{ width: 11 }} />
                            Transfer Admin
                          </button>
                        )}
                        {isAdmin && !isSelf && m.status === UserStatus.ACTIVE && (
                          <button onClick={() => handleDeactivate(m.userId, m.emailAddress)}
                            style={{ padding: '3px 10px', background: 'none', border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, fontSize: fontSize.sm, cursor: 'pointer', color: colors.error }}>
                            Deactivate
                          </button>
                        )}
                        {m.status === UserStatus.INVITED && <UserStatusChip status="invited" />}
                        {isAdmin && m.status !== UserStatus.ACTIVE && m.status !== UserStatus.INVITED && (
                          <button onClick={() => handleReactivate(m.userId)}
                            style={{ padding: '3px 10px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, cursor: 'pointer', color: colors.text }}>
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          padding: spacing.lg,
        }}>
          <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text, marginBottom: spacing.md }}>
            Invite Team Member
          </div>

          {error && (
            <div style={{
              background: colors.errorLight, color: colors.error,
              border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: fontSize.sm, marginBottom: spacing.md,
            }}>{error}</div>
          )}
          {sent && (
            <div style={{
              background: colors.successLight, color: colors.success,
              border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm,
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: fontSize.sm, marginBottom: spacing.md,
            }}>{sent}</div>
          )}

          <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
            <div>
              <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="colleague@example.com"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.base,
                  color: colors.text,
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
                Role
              </label>
              <Select
                value={role}
                onChange={v => setRole(Number(v))}
                options={INVITE_ROLE_OPTIONS}
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={sending || !email.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 20px',
                  background: colors.primary,
                  color: colors.onPrimary,
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.base,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: sending || !email.trim() ? 0.6 : 1,
                }}
              >
                <FontAwesomeIcon icon={faShield} style={{ width: 13 }} />
                {sending ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </form>
        </div>

        <div style={{ marginTop: spacing.xl }}>
          <LeaveTeamCard />
          <DangerZone />
        </div>
      </div>
      {fallbackFor && (
        <DualControlFallbackModal
          email={fallbackFor.email}
          impact={fallbackFor.impact}
          newRoleLabel={fallbackFor.newRole !== undefined ? ROLE_LABELS[fallbackFor.newRole] ?? `Role ${fallbackFor.newRole}` : undefined}
          loading={fallbackSaving}
          error={fallbackError}
          onConfirm={confirmFallback}
          onCancel={() => setFallbackFor(null)}
        />
      )}
      {dialogs.element}
    </div>
  )
}
